import React, { useState, useEffect, useContext } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms/button'
import { RemoteLockModal } from '@/components/pages/setting/guest-room/key-number/remotelock-modal'
import { LoaderContextCreator } from '@/contexts/loader'
import { getSmartLockAccount } from '@/apis/aipass'
import { setHasMiwaLockPlugin } from '@/libs/plugins'
import { AccountContext } from '@/contexts/account'
import { RemoteLockClient, RemoteLockContextCreator } from '@/contexts/remote-lock'
import { AccountInfo, LockType } from '@/models/lock-setting'
import { initializeSelectBoxStyle } from '@/constants/form'
import {
  SmartLockApiCredentialInput,
  SmartLockApiCredentialProps,
} from '@/components/organisms/settings/smart-key/smart-lock-api-credential-input'

type Props = {
  selectRoomKey: LockType
  apiCredential: SmartLockApiCredentialProps
  onChangeType: (v: LockType) => void
}

export const LinkSettingContainer: React.FC<Props> = ({ selectRoomKey, apiCredential, onChangeType }) => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { plugins } = useContext<any>(AccountContext)
  const remoteLockClient = useContext<RemoteLockClient>(RemoteLockContextCreator())
  const hasMiwaLockPlugin = setHasMiwaLockPlugin(plugins)
  const [remoteLockModalOpen, setRemoteLockModalOpen] = useState<boolean>(false)
  const [accountInfos, setAccountInfos] = useState<AccountInfo[]>([])

  const pickSettingByType = (lockType: LockType) => accountInfos.find(i => i.lockType === lockType)

  useEffect(() => {
    remoteLockClient.initialize()
    getSmartLockAccount().then(setAccountInfos)
  }, [plugins])

  return (
    <div css={settingRemoteLockStyle} id="settingRemoteLockStyle">
      <div css={settingRoomKeyTitleStyle}>{t('Cooperation method')}</div>
      <div css={selectRoomKeyButtonStyle}>
        <select name="isKey" value={selectRoomKey} onChange={e => onChangeType(e.target.value as LockType)} css={selectRoomKeyStyle}>
          {/* Because using translation, must pass value to option select */}
          <option value={LockType.Manual}>{t('Manual')}</option>
          <option value={LockType.RemoteLock} disabled={!remoteLockClient.isEnabled}>
            {t('Remote Lock')}
          </option>
          {hasMiwaLockPlugin && <option value={LockType.MiwaLock}>{t('MIWA Lock')}</option>}
          {pickSettingByType(LockType.SwitchBot)?.isEnabled && (
            <option value={LockType.SwitchBot} disabled={!pickSettingByType(LockType.SwitchBot)?.isConnected}>
              Switch Bot
            </option>
          )}
          {pickSettingByType(LockType.KeyPo)?.isEnabled && <option value={LockType.KeyPo}>KEYPo</option>}
          {pickSettingByType(LockType.KeyVox)?.isEnabled && <option value={LockType.KeyVox}>KEYVOX</option>}
        </select>
      </div>
      <SmartLockApiCredentialInput {...apiCredential} />
      {selectRoomKey === LockType.RemoteLock && (
        <Button
          width={remoteLockClient.isConnected ? 74 : 96}
          height={32}
          marginLeft={16}
          buttonType={remoteLockClient.isConnected ? 3 : 1}
          onClick={() => {
            remoteLockClient.isConnected ? setRemoteLockModalOpen(true) : remoteLockClient.openRemoteLockOAuthModal()
          }}
        >
          {remoteLockClient.isConnected ? t('Setting') : t('Alignment')}
        </Button>
      )}
      {remoteLockModalOpen && (
        <RemoteLockModal
          connected={remoteLockClient.isConnected}
          onDisconnect={async () => {
            setIsLoading(true)
            await remoteLockClient.disconnect()
            setIsLoading(false)
            setRemoteLockModalOpen(false)
          }}
          onSync={async () => {
            setIsLoading(true)
            await remoteLockClient.syncDevice()
            setIsLoading(false)
            setRemoteLockModalOpen(false)
          }}
          onCancel={() => setRemoteLockModalOpen(false)}
        />
      )}
    </div>
  )
}

const settingRemoteLockStyle = css({
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  color: '#676767',
  textAlign: 'left',
  marginLeft: 32,
  marginTop: 31,
  marginBottom: 26,
})

const settingRoomKeyTitleStyle = css({
  marginTop: 10,
})

const selectRoomKeyButtonStyle = css({
  marginLeft: 24,
})

const selectRoomKeyStyle = css(initializeSelectBoxStyle, {
  width: '100%',
  height: 32,
  fontSize: 14,
  borderRadius: 19,
  padding: '0 24px 0 16px',
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '95% center',
  backgroundSize: 10,
  display: 'block',
  marginRight: 16.5,
  borderColor: '#CCCCCC',
})
